import React, { useEffect, useState } from "react"
import {
  DEFAULT_PREFS,
  readStoredConsent,
  hasGPCSignal,
  applyConsent,
  commitConsent,
} from "./consentStore"
import { fetchAndStoreUserIP } from "./consentApi"

export default function CookieBanner() {
  const [visible, setVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [prefs, setPrefs] = useState(DEFAULT_PREFS)

  useEffect(() => {
    fetchAndStoreUserIP()

    const stored = readStoredConsent()

    if (stored) {
      applyConsent(stored.prefs)
      return
    }

    if (hasGPCSignal()) {
      const gpcPrefs = { ...DEFAULT_PREFS, functional: true }
      commitConsent(gpcPrefs, { gpcDetected: true, source: "gpc-auto" })
      return
    }

    setVisible(true)
  }, [])

  function acceptAll() {
    const all = {
      necessary: true,
      analytics: true,
      functional: true,
      advertising: true,
      identityResolution: true,
    }
    commitConsent(all, { source: "banner" })
    setVisible(false)
  }

  function rejectNonessential() {
    commitConsent(DEFAULT_PREFS, { source: "banner" })
    setVisible(false)
  }

  function savePreferences() {
    commitConsent(prefs, { source: "settings-update" })
    setVisible(false)
  }

  function toggle(category) {
    setPrefs(prev => ({ ...prev, [category]: !prev[category] }))
  }

  if (!visible) return null

  return (
    <>
      <div
        aria-hidden="true"
        className="fixed inset-0 z-40 bg-black/60 transition-opacity"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-live="polite"
        aria-label="Cookie consent"
        className="fixed inset-x-0 bottom-0 z-50 border-t-4 border-mainOrange bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.08)]"
      >
        <div className="mx-auto max-w-7xl px-6 py-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="font-body text-sm leading-6 text-mainGray lg:max-w-3xl">
              <p>
                We use cookies and similar technologies for website operation,
                analytics, advertising, and identifying potential business
                visitors. We may share identifiers and website activity with
                analytics, advertising, identity resolution, and data
                enrichment providers. Select Accept All, Reject Nonessential,
                or Manage Preferences. See our{" "}
                <a
                  href="/cookie-policy/"
                  className="font-semibold underline hover:text-mainOrange"
                >
                  Cookie Policy
                </a>{" "}
                and{" "}
                <a
                  href="/website-privacy-policy/"
                  className="font-semibold underline hover:text-mainOrange"
                >
                  Privacy Policy
                </a>
                .
              </p>

              {showDetails && (
                <div className="mt-4 space-y-3 border-t border-gray-200 pt-4">
                  <PreferenceRow
                    label="Strictly necessary"
                    description="Operate security, privacy preferences, forms, and essential website functions"
                    checked={true}
                    disabled
                  />
                  <PreferenceRow
                    label="Analytics"
                    description="Understand visits, navigation, content use, and campaign performance"
                    checked={prefs.analytics}
                    onChange={() => toggle("analytics")}
                  />
                  <PreferenceRow
                    label="Functional and communications"
                    description="Enable chat, scheduling, embedded content, and remembered preferences"
                    checked={prefs.functional}
                    onChange={() => toggle("functional")}
                  />
                  <PreferenceRow
                    label="Advertising and targeting"
                    description="Create audiences, measure advertisements, personalize marketing, and retarget visitors"
                    checked={prefs.advertising}
                    onChange={() => toggle("advertising")}
                  />
                  <PreferenceRow
                    label="Identity resolution and enrichment"
                    description="Associate browser or device activity with possible business or professional information"
                    checked={prefs.identityResolution}
                    onChange={() => toggle("identityResolution")}
                  />
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col lg:items-stretch">
              {!showDetails ? (
                <>
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="rounded-full border-2 border-mainOrange bg-mainOrange px-6 py-2.5 font-body text-sm font-semibold uppercase text-white hover:bg-white hover:text-mainOrange"
                  >
                    Accept All
                  </button>
                  <button
                    type="button"
                    onClick={rejectNonessential}
                    className="rounded-full border-2 border-mainGray bg-white px-6 py-2.5 font-body text-sm font-semibold uppercase text-mainGray hover:bg-mainGray hover:text-white"
                  >
                    Reject Nonessential
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDetails(true)}
                    className="px-6 py-2.5 font-body text-sm font-semibold text-mainGray underline underline-offset-2 hover:text-mainOrange"
                  >
                    Manage Preferences
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={savePreferences}
                    className="rounded-full border-2 border-mainOrange bg-mainOrange px-6 py-2.5 font-body text-sm font-semibold uppercase text-white hover:bg-white hover:text-mainOrange"
                  >
                    Save Preferences
                  </button>
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="rounded-full border-2 border-mainGray bg-white px-6 py-2.5 font-body text-sm font-semibold uppercase text-mainGray hover:bg-mainGray hover:text-white"
                  >
                    Accept All
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDetails(false)}
                    className="px-6 py-2.5 font-body text-sm font-semibold text-gray-500 underline underline-offset-2 hover:text-mainOrange"
                  >
                    Back
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function PreferenceRow({ label, description, checked, onChange, disabled }) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="mt-1 h-4 w-4 rounded border-gray-300 accent-mainOrange disabled:opacity-50"
      />
      <span>
        <span className="block font-semibold text-mainGray">
          {label}
          {disabled && (
            <span className="ml-2 text-xs font-normal text-gray-400">
              (always on)
            </span>
          )}
        </span>
        <span className="block text-xs text-gray-500">{description}</span>
      </span>
    </label>
  )
}